
import {type CreateOrderActions, type CreateOrderData, type FUNDING_SOURCE, type OnApproveActions, type OnApproveData, type OnCancelledActions, type PayPalButtonsComponent, type PayPalButtonsComponentOptions, type PayPalNamespace} from '@paypal/paypal-js';
import {$qs, stripHtml} from '../../../../@shared/ts/dom';
import {getErrorString} from '../../../../@shared/ts/error';
import {FeatureFlag} from '../../../../@type/features';
import {disableGatewayOption, displayTokenizeError, getCheckoutForm, observeCheckoutHTML, onCheckoutSubmit, submitOrderPayPalViaFetch, unblockCheckout} from '../../checkout';
import {Feature} from '../../features';
import {cleanupTransactionFields, createPaymentTransaction, insertTransactionFields, updatePaymentTransaction} from '../../transaction';
import {type PaymentData} from '../../../../@shared/ts/paymentAttempt';
import {type PeachPayCheckoutData} from '../../types';
import {type IResponse} from '../../../../@type/response';
import {insertCaptchaFields} from '../../botProtection';

type PayPalInstance = PayPalNamespace & {
	Buttons: (
		options?: PayPalButtonsComponentOptions
	) => PayPalButtonsComponent;
};

declare const peachpay_checkout_data: PeachPayCheckoutData;

abstract class AbstractPayPalPayment {
	private static isOpen = false;

	private static closeActiveFlow(): void {
		AbstractPayPalPayment.isOpen = false;
	}

	protected paypal: PayPalInstance | null;

	private button: PayPalButtonsComponent | null;
	private mountContainer: HTMLDivElement | null;
	private isMounted: boolean;
	private isEligible: boolean;
	private submitHandlerRegistered: boolean;

	private currentPaymentDetails: PayPalPaymentData | null;

	constructor() {
		this.paypal = null;
		this.button = null;
		this.isEligible = false;
		this.isMounted = false;
		this.submitHandlerRegistered = false;
		this.mountContainer = null;
		this.currentPaymentDetails = null;
	}

	public init(paypal: PayPalInstance): Promise<void> | void {
		this.paypal = paypal;
		this.button = this.createButtonComponent();

		const eligible = this.button?.isEligible() ?? false;

		if (eligible) {
			this.setGatewayOptionVisibility(true);
			this.isEligible = true;
		} else {
			observeCheckoutHTML(disableGatewayOption(this.gatewayId()));
			disableGatewayOption(this.gatewayId())();
			this.isEligible = false;
		}
	}

	public mount() {
		if (!this.paypal || !this.isEligible || AbstractPayPalPayment.isOpen) {
			return;
		}

		if (this.mountContainer && !document.contains(this.mountContainer)) {
			this.mountContainer = null;
			this.button = null;
			this.isMounted = false;
		}

		if (this.isMounted) {
			return;
		}

		if (!this.button) {
			this.button = this.createButtonComponent();
		}

		if (!this.submitHandlerRegistered) {
			const $checkoutForm = getCheckoutForm();
			if ($checkoutForm) {
				onCheckoutSubmit($checkoutForm, {
					async peachpay_paypal_credit() {
						return false;
					},
					async peachpay_paypal_paylater() {
						return false;
					},
					async peachpay_paypal_wallet() {
						return false;
					},
					async peachpay_paypal_venmo() {
						return false;
					},
					async peachpay_paypal_card() {
						return false;
					},
				});
				this.submitHandlerRegistered = true;
			}
		}

		if (this.mountContainer) {
			this.mountContainer?.classList.remove('hide');
		} else {
			const $wcButton = $qs('.place-order,.woocommerce-terms-and-conditions-wrapper');

			if (!$wcButton) {
				return;
			}

			this.mountContainer = document.createElement('div');
			this.mountContainer.setAttribute('id', this.gatewayId() + '_button_container');
			$wcButton?.insertAdjacentElement('beforeend', this.mountContainer);
			this.button?.render('#' + this.gatewayId() + '_button_container').catch((error: unknown) => {
				console.error(error);
			});
		}

		this.isMounted = true;
	}

	public unmount() {
		if (AbstractPayPalPayment.isOpen) {
			return;
		}

		if (!this.isMounted) {
			return;
		}

		this.mountContainer?.classList.add('hide');
		this.isMounted = false;
	}

	public displayPaymentError(message: string): void {
		displayTokenizeError(message);
	}

	public abstract gatewayId(): string;

	protected abstract fundingType(): FUNDING_SOURCE;

	protected abstract buttonStyle(): PayPalButtonsComponentOptions['style'];

	private setGatewayOptionVisibility(isVisible: boolean): void {
		const option = document.querySelector<HTMLElement>('li.wc_payment_method.payment_method_' + this.gatewayId());
		if (!option) {
			return;
		}

		option.classList.toggle('hide', !isVisible);
	}

	private getCustomerFacingErrorMessage(error: unknown): string {
		const rawMessage = error instanceof Error ? error.message : getErrorString(error);
		const normalizedMessage = stripHtml(rawMessage, null)
			.replace(/\s+/g, ' ')
			.trim();

		return normalizedMessage || 'Checkout could not be completed. Please review the required fields and try again.';
	}

	private createButtonComponent(): PayPalButtonsComponent | null {
		const gatewayId = this.gatewayId();
		const gateway = this;
		// eslint-disable-next-line new-cap
		return this.paypal?.Buttons({
			fundingSource: this.fundingType(),
			style: this.buttonStyle(),
			onClick() {
				AbstractPayPalPayment.isOpen = true;
			},
			async createOrder(_data: CreateOrderData, _actions: CreateOrderActions) {
				const $checkoutForm = getCheckoutForm();
				if (!$checkoutForm) {
					AbstractPayPalPayment.closeActiveFlow();
					throw new Error('Checkout form not found.');
				}

				const transactionID = await createPaymentTransaction(gatewayId);
				if (!transactionID) {
					AbstractPayPalPayment.closeActiveFlow();
					throw new Error('Could not create payment transaction.');
				}

				insertTransactionFields($checkoutForm, transactionID);
				await insertCaptchaFields();

				try {
					const result = peachpay_checkout_data.page.is_order_pay
						? await submitOrderPayOrder()
						: await submitOrderPayPalViaFetch($checkoutForm) as PayPalPaymentData;

					gateway.currentPaymentDetails = result;
					return result.data.id;
				} catch (error) {
					AbstractPayPalPayment.closeActiveFlow();
					const note = typeof error === 'string' ? error : getErrorString(error);
					const customerMessage = gateway.getCustomerFacingErrorMessage(error);
					console.error('[PP-PayPal]', gatewayId, 'createOrder: failed', note);
					await updatePaymentTransaction(transactionID, {
						paymentStatus: 'unperformed',
						orderStatus: 'creationfailed',
						note,
					});
					gateway.displayPaymentError(customerMessage);
					unblockCheckout($checkoutForm);
					throw new Error(customerMessage);
				}
			},
			async onApprove(_data: OnApproveData, actions: OnApproveActions) {
				if (!gateway.currentPaymentDetails?.order_id) {
					AbstractPayPalPayment.closeActiveFlow();
					console.error('Unknown Woocommerce Order');
					actions.restart();
					return;
				}

				const approveURL = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'approve_order_url');

				if (!approveURL) {
					AbstractPayPalPayment.closeActiveFlow();
					console.error('Missing required dependencies to Capture/Confirm PayPal order.');
					actions.restart();
					return;
				}

				const formData = new FormData();
				formData.append('order_id', gateway.currentPaymentDetails.order_id);

				const response = await fetch(approveURL, {
					method: 'POST',
					body: formData,
				});

				const json = await response.json() as IResponse<void>;

				if (!json.success) {
					AbstractPayPalPayment.closeActiveFlow();
					console.error(json.message);
					actions.restart();
					return;
				}

				actions.redirect(gateway.currentPaymentDetails.success_url);
			},
			async onCancel(data: Record<string, unknown>, _actions: OnCancelledActions) {
				AbstractPayPalPayment.closeActiveFlow();
				if (gateway.currentPaymentDetails?.transaction_id) {
					await updatePaymentTransaction(gateway.currentPaymentDetails.transaction_id, {
						paymentStatus: 'CANCELED',
						note: 'PayPal checkout was canceled by customer.',
					});
				}

				gateway.currentPaymentDetails = null;
				cleanupTransactionFields();

				const $checkoutForm = getCheckoutForm();
				if ($checkoutForm) {
					unblockCheckout($checkoutForm);
				} else {
					console.error(data);
				}
			},
			async onError(error: Record<string, unknown>) {
				AbstractPayPalPayment.closeActiveFlow();
				console.error(error);

				if (gateway.currentPaymentDetails?.transaction_id) {
					await updatePaymentTransaction(gateway.currentPaymentDetails.transaction_id, {
						note: getErrorString(error),
					});
				}

				gateway.currentPaymentDetails = null;

				const $checkoutForm = getCheckoutForm();
				if ($checkoutForm) {
					unblockCheckout($checkoutForm);
				}
			},
		}) ?? null;
	}
}

async function submitOrderPayOrder() {
	const $orderPayForm = document.querySelector<HTMLFormElement>('form#order_review')!;

	const response = await fetch(
		window.location.href,
		{
			method: 'POST',
			body: new FormData($orderPayForm),
		},
	);

	const url = new URL(response.url);

	const data = url.searchParams.get('payment_data');
	if (!data) {
		throw new Error('Invalid url query payment_data: ' + url.search);
	}

	return JSON.parse(atob(decodeURIComponent(data))) as PayPalPaymentData;
}

interface PayPalPaymentData extends PaymentData {
	'data': {
		'id': string;
	};
}

export {
	AbstractPayPalPayment,
	type PayPalInstance,
};
